import {Location} from "@angular/common";
import {Injectable, Type} from "@angular/core";
import {GuardsCheckEnd, NavigationBehaviorOptions, NavigationEnd, NavigationStart, Route, Router} from "@angular/router";
import {CacheItem, SessionStorageCacheStore} from "framework/services/cache.service";

import {Logger, LoggingService} from "framework/services/logging.service";
import {Lookup} from "framework/services/utils.service";
import {BaseComponent} from "framework/ui/components";
import {FullscreenViewService} from "framework/ui/fullscreen-view/fullscreen-view.service";
import {Future} from "framework/utils/concurrency";

import {Subject} from "rxjs";


@Injectable()
export class AppNavigationService
{
    private static readonly CACHE_PREFIX: string     = "CACHE_NAVIGATION";
    private static readonly CACHE_LAST_PATH: string  = "LAST_PATH";
    private static readonly CACHE_LAST_STATE: string = "LAST_STATE";

    readonly logger: Logger;

    private m_currentRouteParams: any;
    private m_ignoreRouteAsPrevious: boolean = false;

    private m_restoringHistory: NavigationState;
    private m_currentHistory: NavigationState;
    private routeBreadcrumbs: BreadcrumbItem<string>[] = [];
    private static readonly FULLSCREEN_CRUMB_KEY       = "__fullscreen__";

    get hasNavigated(): boolean
    {
        if (!this.m_currentHistory) return false;
        return !!this.m_currentHistory.previous;
    }

    get breadcrumbs(): BreadcrumbItem<string>[]
    {
        return this.computeBreadcrumbs();
    }

    set breadcrumbs(value: BreadcrumbItem<string>[])
    {
        this.routeBreadcrumbs = [...(value || [])];
        this.updateFullscreenBreadcrumbs();
    }

    private m_breadcrumbLabel: string;
    breadcrumbLabelChanged = new Subject<string>();
    breadcrumbsChanged     = new Subject<BreadcrumbItem<string>[]>();

    /**
     * Handler for home icon click - set by app layer to navigate to user's landing page
     */
    public homeClickHandler: () => void;

    private m_currentState: Lookup<any>;
    private m_cache: SessionStorageCacheStore = new SessionStorageCacheStore(AppNavigationService.CACHE_PREFIX);

    get currentState(): Lookup<any>
    {
        return this.m_currentState;
    }

    set breadcrumbCurrentLabel(val: string)
    {
        this.m_breadcrumbLabel = val;
        this.breadcrumbLabelChanged.next(val);
    }

    get breadcrumbCurrentLabel(): string
    {
        return this.m_breadcrumbLabel;
    }

    get path(): string[]
    {
        return this.location.path()
                   .split("/");
    }

    get lastPathSegment(): string
    {
        const path = this.path;
        return path.length ? path[path.length - 1] : path[0];
    }

    constructor(private router: Router,
                private location: Location,
                private fullscreenService: FullscreenViewService,
                logService: LoggingService)
    {
        this.logger = logService.getLogger(AppNavigationService);

        let firstRoute: boolean = true;

        // subscribe to route change
        router.events.subscribe((val) =>
                                {
                                    if (val instanceof NavigationStart)
                                    {
                                        // If first load, attempt to restore state from session storage cache
                                        if (firstRoute)
                                        {
                                            try
                                            {
                                                const lastPath = this.m_cache.get(AppNavigationService.CACHE_LAST_PATH);
                                                if (lastPath.getValue<string>() === val.url)
                                                {

                                                    const lastState     = this.m_cache.get(AppNavigationService.CACHE_LAST_STATE);
                                                    this.m_currentState = lastState.getValue();
                                                }

                                            }
                                            catch (e)
                                            {
                                                this.m_currentState = null;
                                            }
                                            finally
                                            {
                                                this.m_cache.remove(AppNavigationService.CACHE_LAST_PATH);
                                                this.m_cache.remove(AppNavigationService.CACHE_LAST_STATE);
                                                firstRoute = false;
                                            }
                                        }
                                        else
                                        {
                                            // If not restored from the session storage cache, get state from current navigation
                                            this.m_currentState = router.getCurrentNavigation()?.extras?.state;
                                        }

                                        if (this.m_restoringHistory === undefined)
                                        {
                                            if (val.restoredState)
                                            {
                                                this.logger.debug(`Back navigation to ${val.url} for ${val.navigationTrigger}`);

                                                // If not restored from cache or current navigation, restore from navigation history
                                                if (!this.m_currentState)
                                                {
                                                    this.m_currentState = val.restoredState;
                                                }

                                                //
                                                // This is a Back/Forward kind of navigation.
                                                // Remember the ID of the original navigation, we'll use it to update the history state.
                                                //
                                                let history = this.m_currentHistory?.findId(val.restoredState.navigationId, new Set<number>());
                                                if (!history)
                                                {
                                                    let prev = this.m_currentHistory?.previous;
                                                    let next = this.m_currentHistory?.next;

                                                    if (next?.url == val.url)
                                                    {
                                                        history = next;
                                                    }
                                                    else if (prev?.url == val.url)
                                                    {
                                                        history = prev;
                                                    }
                                                }

                                                this.m_restoringHistory = history;
                                            }
                                        }

                                        if (this.fullscreenService.getDepth() > 0)
                                        {
                                            this.fullscreenService.closeAll();
                                        }
                                    }

                                    if (val instanceof NavigationEnd)
                                    {
                                        let history: NavigationState;

                                        if (this.m_restoringHistory)
                                        {
                                            //
                                            // This is the result of a Pop navigation.
                                            // Instead of creating a new entry in the history, update the target one.
                                            //
                                            history     = this.m_restoringHistory;
                                            history.id  = val.id;
                                            history.url = val.urlAfterRedirects;
                                        }
                                        else
                                        {
                                            history                       = new NavigationState();
                                            history.id                    = val.id;
                                            history.url                   = val.urlAfterRedirects;
                                            history.ignoreRouteAsPrevious = this.m_ignoreRouteAsPrevious;
                                            history.navigationState       = this.m_currentState;

                                            history.previous = this.m_currentHistory;

                                            if (this.m_currentHistory) this.m_currentHistory.next = history;
                                        }

                                        let isBackNavigation    = this.m_currentHistory?.previous == history;
                                        let isForwardNavigation = this.m_currentHistory?.next == history;

                                        this.m_restoringHistory = undefined;
                                        this.m_currentHistory   = history;

                                        history.limitDepth(100);

                                        // Update state in session storage cache
                                        this.m_cache.set(new CacheItem(AppNavigationService.CACHE_LAST_PATH, this.m_currentHistory.url));
                                        this.m_cache.set(new CacheItem(AppNavigationService.CACHE_LAST_STATE, this.currentState));

                                        if (isBackNavigation && history.ignoreRouteAsPrevious && history.previous)
                                        {
                                            this.logger.debug(`Skipping Back navigation to ${history.id} ${history.url}`);

                                            this.location.back();
                                        }

                                        if (isForwardNavigation && history.ignoreRouteAsPrevious)
                                        {
                                            this.logger.debug(`Skipping Forward navigation to ${history.id} ${history.url}`);

                                            this.location.forward();
                                        }

                                    }

                                    if (val instanceof GuardsCheckEnd)
                                    {
                                        const id = val.state.root.queryParams.navId;
                                        if (id)
                                        {
                                            const contextObj    = localStorage.getItem(id);
                                            this.m_currentState = JSON.parse(contextObj);
                                            localStorage.removeItem(id);
                                        }
                                    }
                                });

        this.fullscreenService.currentView$.subscribe(() =>
                                                      {
                                                          this.updateFullscreenBreadcrumbs();
                                                      });
    }

    //#region Navigation

    /**
     * Navigate down the navigation hierarchy by appending url segments and params to the current route.
     * @param segments
     * @param params
     */
    public async push(segments: any[],
                      params?: PathParam[])
    {
        await this.go(this.router.url, segments, params);
    }

    private fetchTargetHistory(): NavigationState
    {
        let targetHistory = this.m_currentHistory?.previous;
        while (targetHistory && targetHistory.ignoreRouteAsPrevious)
        {
            targetHistory = targetHistory.previous;
        }

        if (targetHistory)
        {
            this.m_restoringHistory = targetHistory;
            return targetHistory;
        }
        return undefined;
    }

    public fallbackRoute()
    {
        // To determine if there's history
        if (document.referrer && document.referrer.indexOf(window.location.origin) >= 0 && document.referrer.indexOf("/site/login") < 0)
        {
            this.location.back();
        }
        else
        {
            window.location.pathname = "/account/profile";
        }
    }

    /**
     * Navigate back up the navigation hierarchy.
     */
    public async pop()
    {
        // Add a little delay, to allow database change notifications to trickle through.
        await Future.delayed(150);

        let state = this.fetchTargetHistory();

        if (state)
        {
            await this.router.navigateByUrl(state.url, state.navigationState ? {state: state.navigationState} : undefined);
        }
        else
        {
            this.fallbackRoute();
        }
    }

    /**
     * Go to the specific url.
     * @param url
     * @param segments
     * @param params
     * @param extras
     * @param updateUrlOnly
     */
    public async go(url: string,
                    segments?: any[],
                    params?: PathParam[],
                    extras?: NavigationBehaviorOptions,
                    updateUrlOnly: boolean = false)
    {
        let formattedUrl = this.formatUrl(url, segments, params);

        if (updateUrlOnly)
        {
            this.logger.info(`Setting parameters ${formattedUrl}`);
            this.location.go(formattedUrl);
        }
        else
        {
            this.logger.info(`Navigating to ${formattedUrl}`);
            await this.router.navigateByUrl(formattedUrl, extras);
        }
    }

    /**
     * Set params
     */
    public async setParams(params: PathParam[])
    {
        let url = this.path.join("/");
        // Strip off any existing params
        url     = url.split(";")[0];

        return this.go(url, [], params, null, true);
    }

    /**
     * Format a url given the root url, additional url segments, and query params
     * @param rootUrl
     * @param segments
     * @param params
     */
    public formatUrl(rootUrl: string,
                     segments?: any[],
                     params?: { param: string, value: any }[])
    {
        // Strip off existing parameters and use the root url
        rootUrl = rootUrl.split(";")[0];

        let urlSegments = rootUrl.split("/");

        if (segments)
        {
            let encodedSegments: any[] = segments.map((segment) =>
                                                      {
                                                          return encodeURIComponent(<string>segment);
                                                      });

            for (let encodedSegment of encodedSegments)
            {
                if (encodedSegment == "..")
                {
                    urlSegments.pop();
                }
                else
                {
                    urlSegments.push(encodedSegment);
                }
            }

            rootUrl = urlSegments.join("/");
        }
        if (params)
        {
            let encodedParams: string[] = params.filter((param) => !!param?.value)
                                                .map((param) =>
                                                     {
                                                         return encodeURIComponent(param.param) + "=" + encodeURIComponent(param.value);
                                                     });
            // Empty or all-null params must not leave a dangling ";" on the url
            if (encodedParams.length)
            {
                rootUrl += ";" + encodedParams.join(";");
            }
        }
        return rootUrl;
    }

    //#endregion

    //#region Breadcrumb Management

    /**
     * Set the breadcrumbs to display.
     * @param breadcrumbs
     * @param currentRouteParams
     * @param ignoreRouteAsPrevious
     */
    public setBreadcrumbs(breadcrumbs: BreadcrumbItem<string>[],
                          currentRouteParams: any,
                          ignoreRouteAsPrevious: boolean)
    {
        this.routeBreadcrumbs        = [...breadcrumbs];
        this.m_currentRouteParams    = currentRouteParams;
        this.m_ignoreRouteAsPrevious = ignoreRouteAsPrevious;
        this.updateFullscreenBreadcrumbs();
    }

    /**
     * Dynamically add a new breadcrumb to display.
     * @param title
     * @param url
     */
    public addBreadcrumb(title: string,
                         url: string)
    {
        if (title)
        {
            this.routeBreadcrumbs.push(new BreadcrumbItem<string>(title, url));
            this.updateFullscreenBreadcrumbs();
        }
    }

    public async previousBreadcrumb()
    {
        if (this.breadcrumbs?.length)
        {
            for (let i = this.breadcrumbs.length - 1; i >= 0; i--)
            {
                const breadcrumb = this.breadcrumbs[i];
                if (breadcrumb.data) return await this.breadcrumb(breadcrumb);
            }
        }
    }

    public clearCurrentState(): void
    {
        this.m_currentState = null;
    }

    private updateFullscreenBreadcrumbs(): void
    {
        this.breadcrumbCurrentLabel = this.fullscreenService.currentState?.label ?? null;
        this.breadcrumbsChanged.next(this.computeBreadcrumbs());
    }

    private computeBreadcrumbs(): BreadcrumbItem<string>[]
    {
        const overlayLabels = this.fullscreenService.getOverlayLabels();
        if (!overlayLabels.length && this.fullscreenService.getDepth() === 0) return [...this.routeBreadcrumbs];

        const overlayCrumbs = overlayLabels.map(label => new BreadcrumbItem<string>(label, AppNavigationService.FULLSCREEN_CRUMB_KEY));

        return [
            ...this.routeBreadcrumbs,
            ...overlayCrumbs
        ];
    }

    /**
     * Navigate to the associated breadcrumb url
     * @param item
     * @param params
     */
    public async breadcrumb(item: BreadcrumbItem<string>,
                            params?: { param: string, value: any }[])
    {
        if (item.data === AppNavigationService.FULLSCREEN_CRUMB_KEY)
        {
            this.fullscreenService.backToView(item.label);
            return;
        }

        if (!item.data)
        {
            return;
        }

        if (this.fullscreenService.getDepth() > 0)
        {
            this.fullscreenService.closeAll();
        }

        // process url values
        let finalUrl = item.data;
        let segments = item.data.split("/");
        for (let segment of segments)
        {
            if (segment[0] == ":")
            {
                let paramName  = segment.replace(":", "");
                let paramValue = this.m_currentRouteParams[paramName];
                finalUrl       = finalUrl.replace(segment, paramValue);
            }
        }

        // go to breadcrumb url
        await this.go(finalUrl, [], params);
    }


    public async navigateToPage(portalPath: string,
                                context: string)
    {
        const id = crypto.randomUUID();
        localStorage.setItem(id, context);
        window.open(portalPath + "?navId=" + id, "_blank");
    }

    //#endregion
}

export class PathParam
{
    constructor(public readonly param: string,
                public readonly value: string)
    {}
}

/**
 * Holds information for items in the breadcrumbs
 */
export class BreadcrumbItem<T>
{
    constructor(public label: string,
                public data: T)
    {}

    public get canInteract(): boolean
    {
        return !!this.data;
    }

    public clone(): BreadcrumbItem<T>
    {
        return new BreadcrumbItem<T>(this.label, this.data);
    }
}

class NavigationState
{
    public id: number;
    public url: string;
    public ignoreRouteAsPrevious: boolean;

    public navigationState: Lookup<any>;

    public previous: NavigationState;
    public next: NavigationState;

    findId(id: number,
           visited: Set<number>): NavigationState
    {
        if (visited.has(this.id))
        {
            return null;
        }

        if (this.id === id) return this;

        visited.add(this.id);

        if (this.previous)
        {
            let res = this.previous.findId(id, visited);
            if (res) return res;
        }

        if (this.next)
        {
            let res = this.next.findId(id, visited);
            if (res) return res;
        }

        return null;
    }

    limitDepth(depth: number)
    {
        if (depth <= 0)
        {
            this.previous = null;
            return;
        }

        if (this.previous)
        {
            this.previous.limitDepth(depth - 1);
        }
    }
}


export interface TypedRouteData
{
    title: string,
    greyBackground?: boolean,
    hideAccountMaster?: boolean,
    hideStandardLayout?: boolean,
    ignoreAsPrevious?: boolean,
    breadcrumbs?: Array<BreadcrumbItem<string>>,
    suppressNotificationPoll?: boolean
}

export interface TypedRoute extends Route
{
    data?: TypedRouteData;
    children?: TypedRoute[];
}

export type TypedRoutes = TypedRoute[];

export class RouteBuilder
{
    public static build(base: string,
                        routes: RouteDefinition): TypedRoute[]
    {
        const root = new RouteDefinitionExt(null, "", routes);
        const all  = root.flatten();

        return all.map((route) =>
                       {
                           const canMatch = route.def.canMatch;
                           return {
                               path     : route.path(),
                               component: route.component(),
                               ...(canMatch ? {canMatch} : {}),
                               data     : {
                                   title         : route.title(),
                                   greyBackground: true,
                                   breadcrumbs   : route.ancestors()
                                                        .map((r) => r.breadcrumb(base))
                               }
                           };
                       });
    };
}

class RouteDefinitionExt
{
    constructor(public parent: RouteDefinitionExt,
                public pathSegment: string,
                public def: RouteDefinition)
    {}

    public hasChildren(): boolean
    {
        return !!this.def.children;
    }

    public getChildren(): RouteDefinitionExt[]
    {
        if (!this.hasChildren()) return null;

        return Object.entries(this.def.children)
                     .map((route) => new RouteDefinitionExt(this, route[0], route[1]));
    }

    public flatten(): RouteDefinitionExt[]
    {
        const children = this.getChildren();
        const filtered = children?.filter((child) => !!child) ?? [];
        const expanded = filtered.map((child) => child.flatten());
        return [
            this,
            ...expanded.flat()
        ];
    }

    public component(): Type<BaseComponent>
    {
        return this.def.component;
    }

    public path(): string
    {
        let path = this.ancestors()
                       .map((route) => route.pathSegment)
                       .join("/");

        return path[0] === "/" ? path.substring(1) : path;
    }

    public title(): string
    {
        return this.def.title;
    }

    public breadcrumb(base: string): BreadcrumbItem<string>
    {
        const path = this.hasChildren() ? [
            base,
            this.path()
        ].filter(Boolean)
         .join("/") : null;

        return new BreadcrumbItem<string>(this.title(), path);
    }

    public ancestors(): RouteDefinitionExt[]
    {
        const ancestors: RouteDefinitionExt[] = [];
        let current: RouteDefinitionExt       = this;

        while (!!current)
        {
            ancestors.unshift(current);
            current = current.parent;
        }

        return ancestors;
    }
}

export interface RouteDefinition
{
    component: Type<BaseComponent>,
    title: string,
    children?: Lookup<RouteDefinition>,
    canMatch?: Route["canMatch"]
}
